/**
 * Security Utilities - OWASP Compliant
 * Implements security best practices for frontend application
 */

// 1. INPUT VALIDATION & SANITIZATION
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  // Remove HTML tags and dangerous characters
  return input
    .replace(/[<>]/g, '') // Remove < and >
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .trim();
};

export const validateEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(sanitizeInput(email));
};

export const validatePhone = (phone) => {
  const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
  return phoneRegex.test(sanitizeInput(phone));
};

export const validateZipCode = (zip) => {
  // Allows various international formats
  const zipRegex = /^[A-Z0-9\s-]{3,10}$/i;
  return zipRegex.test(sanitizeInput(zip));
};

export const validateCardNumber = (cardNumber) => {
  // Basic Luhn algorithm check
  const sanitized = cardNumber.replace(/\s/g, '');
  if (!/^\d{13,19}$/.test(sanitized)) return false;
  
  let sum = 0;
  let isEven = false;
  
  for (let i = sanitized.length - 1; i >= 0; i--) {
    let digit = parseInt(sanitized[i]);
    
    if (isEven) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    
    sum += digit;
    isEven = !isEven;
  }
  
  return sum % 10 === 0;
};

// 2. XSS PREVENTION
export const escapeHTML = (str) => {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};

export const sanitizeURL = (url) => {
  try {
    const parsed = new URL(url);
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return '#';
    }
    return url;
  } catch {
    return '#';
  }
};

// 3. SECURE LOCAL STORAGE
// NOTE: localStorage is intentionally not used (Firebase-only persistence).
// This keeps an in-memory API for any remaining call-sites.
const memoryStorage = new Map();

export const secureStorage = {
  setItem: (key, value) => {
    try {
      memoryStorage.set(String(key), value);
      return true;
    } catch (error) {
      console.error('Storage error:', error);
      return false;
    }
  },

  getItem: (key) => {
    try {
      return memoryStorage.get(String(key)) ?? null;
    } catch (error) {
      console.error('Storage error:', error);
      return null;
    }
  },

  removeItem: (key) => {
    memoryStorage.delete(String(key));
  },

  clear: () => {
    memoryStorage.clear();
  }
};

// 4. CSRF PROTECTION
export const generateCSRFToken = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

export const getCSRFToken = () => {
  let token = sessionStorage.getItem('csrf_token');
  if (!token) {
    token = generateCSRFToken();
    sessionStorage.setItem('csrf_token', token);
  }
  return token;
};

// 5. RATE LIMITING (Client-side helper)
const rateLimitStore = new Map();

export const checkRateLimit = (action, maxAttempts = 5, windowMs = 60000) => {
  const now = Date.now();
  const key = `ratelimit_${action}`;
  
  let attempts = rateLimitStore.get(key) || [];
  
  // Remove old attempts outside the window
  attempts = attempts.filter(timestamp => now - timestamp < windowMs);
  
  if (attempts.length >= maxAttempts) {
    return {
      allowed: false,
      remainingAttempts: 0,
      resetTime: new Date(attempts[0] + windowMs)
    };
  }
  
  attempts.push(now);
  rateLimitStore.set(key, attempts);
  
  return {
    allowed: true,
    remainingAttempts: maxAttempts - attempts.length,
    resetTime: new Date(now + windowMs)
  };
};

// 6. SECURE HEADERS HELPER (for API calls)
export const getSecureHeaders = () => {
  return {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'X-CSRF-Token': getCSRFToken(),
    // Don't send sensitive data in headers
  };
};

// 7. PASSWORD STRENGTH VALIDATOR (if you add auth later)
export const validatePasswordStrength = (password) => {
  const minLength = 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  
  const strength = {
    isValid: password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers,
    length: password.length >= minLength,
    hasUpperCase,
    hasLowerCase,
    hasNumbers,
    hasSpecialChar,
    score: 0
  };
  
  if (strength.length) strength.score++;
  if (strength.hasUpperCase) strength.score++;
  if (strength.hasLowerCase) strength.score++;
  if (strength.hasNumbers) strength.score++;
  if (strength.hasSpecialChar) strength.score++;
  
  return strength;
};

// 8. PREVENT CLICKJACKING
export const preventClickjacking = () => {
  if (window.self !== window.top) {
    // Page is in an iframe, redirect to prevent clickjacking
    window.top.location = window.self.location;
  }
};

// 9. SANITIZE FORM DATA
export const sanitizeFormData = (formData) => {
  const sanitized = {};
  
  for (const [key, value] of Object.entries(formData)) {
    if (typeof value === 'string') {
      sanitized[key] = sanitizeInput(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

// 10. DETECT AND PREVENT COMMON ATTACKS
export const detectSuspiciousActivity = (input) => {
  const suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+=/i,
    /<iframe/i,
    /eval\(/i,
    /document\.cookie/i,
    /\.\.\/\.\.\//,  // Path traversal
    /union.*select/i, // SQL injection attempt
    /;\s*drop\s+table/i, // SQL injection
  ];
  
  return suspiciousPatterns.some(pattern => pattern.test(input));
};

// 11. CONTENT SECURITY POLICY HELPER
export const getCSPMeta = () => {
  return {
    'default-src': "'self'",
    'script-src': "'self' 'unsafe-inline'",
    'style-src': "'self' 'unsafe-inline' https://fonts.googleapis.com",
    'img-src': "'self' data: https: blob:",
    'font-src': "'self' https://fonts.gstatic.com",
    'connect-src': "'self' https://ipapi.co",
    'frame-ancestors': "'none'",
    'base-uri': "'self'",
    'form-action': "'self'"
  };
};

// 12. SECURE RANDOM ID GENERATOR
export const generateSecureId = () => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// 13. SESSION TIMEOUT HANDLER
let sessionTimeout;

export const initSessionTimeout = (timeoutMs = 30 * 60 * 1000, onTimeout) => {
  const resetTimeout = () => {
    clearTimeout(sessionTimeout);
    sessionTimeout = setTimeout(() => {
      if (onTimeout) onTimeout();
    }, timeoutMs);
  };
  
  // Reset on user activity
  ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(event => {
    document.addEventListener(event, resetTimeout, { passive: true });
  });
  
  resetTimeout();
};

// 14. SAFE JSON PARSE
export const safeJSONParse = (str, fallback = null) => {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
};

// Export all utilities
export default {
  sanitizeInput,
  validateEmail,
  validatePhone,
  validateZipCode,
  validateCardNumber,
  escapeHTML,
  sanitizeURL,
  secureStorage,
  generateCSRFToken,
  getCSRFToken,
  checkRateLimit,
  getSecureHeaders,
  validatePasswordStrength,
  preventClickjacking,
  sanitizeFormData,
  detectSuspiciousActivity,
  getCSPMeta,
  generateSecureId,
  initSessionTimeout,
  safeJSONParse
};
